import styles from './index.module.css'

import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import { LuSearch } from '@react-icons/all-files/lu/LuSearch'
import { LuX } from '@react-icons/all-files/lu/LuX'
import React, { useEffect, useMemo, useState } from 'react'

import mixpanel from '@disco/api/mixpanel'
import { createCustomNativeTag } from '@disco/state-management/actions/CustomNativeTagActions'
import {
  addNativeTagToTracks,
  removeNativeTagFromTracks,
} from '@disco/state-management/actions/PlaylistTrackActions'
import { matchTag } from '@disco/utils/tagSearch'

import type { NativeCategory, NativeTag, TrackId } from 'types/track'

import { isAiTagPlaceholder } from 'components/common/add-track-tags-modal/ai-tag-placeholder'
import TagChip from 'components/common/add-track-tags-modal/tag-chip'
import { Button, IconButton, Popover, Text, TextField } from 'components/ds'

import { useDispatch } from 'hooks/use-redux'

const byName = (a: NativeTag, b: NativeTag) => a.name.localeCompare(b.name)

type Step = 'browse' | 'create'

type Props = {
  category: NativeCategory
  trackIds: TrackId[]
  /** Ids of this category's tags already assigned to at least one track
   * in the selection — shown as selected (outline) in the picker. */
  assignedTagIds: number[]
  colorIndicatorsEnabled: boolean
  isBulk: boolean
}

/**
 * Per-category "+ Add" affordance for the edit-metadata Tags tab: a
 * scoped popover listing only that category's tags as toggleable chips,
 * with inline filtering. Toggling applies immediately (add/remove
 * dispatches right away) — there's no staged Done-to-save step, unlike
 * the full "Add track tags" modal.
 *
 * Creating a tag swaps the whole popover to a dedicated, minimal
 * create view (name field + Create/Cancel) rather than growing the
 * browse view's footer — it reads as a clean, focused step rather than
 * clutter stacked under the existing chip list.
 */
const CategoryTagPicker = ({
  category,
  trackIds,
  assignedTagIds,
  colorIndicatorsEnabled,
  isBulk,
}: Props) => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<Step>('browse')
  const [query, setQuery] = useState('')
  const [orderIds, setOrderIds] = useState<number[]>(assignedTagIds)
  const [createName, setCreateName] = useState('')
  const [createSaving, setCreateSaving] = useState(false)
  const [freshTagId, setFreshTagId] = useState<number | null>(null)

  useEffect(() => {
    if (freshTagId === null) return
    const timer = setTimeout(() => setFreshTagId(null), 400)
    return () => clearTimeout(timer)
  }, [freshTagId])

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (next) {
      setOrderIds(assignedTagIds)
      setQuery('')
      setStep('browse')
    }
  }

  const toggleTag = (tag: NativeTag) => {
    if (assignedTagIds.includes(tag.id)) {
      dispatch(removeNativeTagFromTracks(trackIds, tag))
      return
    }
    mixpanel.sendPureEvent(
      isBulk ? 'Add multiple native tags' : 'Add native tag',
      { Tags: [tag.name] },
    )
    dispatch(addNativeTagToTracks(trackIds, tag))
  }

  const openCreate = (prefill: string) => {
    setCreateName(prefill)
    setStep('create')
  }

  const backToBrowse = () => {
    setStep('browse')
    setCreateName('')
  }

  const commitCreate = async () => {
    const trimmed = createName.trim()
    if (!trimmed) return
    const existing = category.native_tags.find(
      tag => tag.name.toLowerCase() === trimmed.toLowerCase(),
    )
    if (existing) {
      if (!assignedTagIds.includes(existing.id)) {
        mixpanel.sendPureEvent(
          isBulk ? 'Add multiple native tags' : 'Add native tag',
          { Tags: [existing.name] },
        )
        dispatch(addNativeTagToTracks(trackIds, existing))
      }
      setOrderIds(ids =>
        ids.includes(existing.id) ? ids : [...ids, existing.id],
      )
      setFreshTagId(existing.id)
      backToBrowse()
      setQuery('')
      return
    }

    setCreateSaving(true)
    try {
      const tag = await dispatch(createCustomNativeTag(trimmed, category.id))
      mixpanel.sendPureEvent(
        isBulk ? 'Add multiple native tags' : 'Add native tag',
        { Tags: [tag.name] },
      )
      dispatch(addNativeTagToTracks(trackIds, tag))
      setOrderIds(ids => [...ids, tag.id])
      setFreshTagId(tag.id)
      backToBrowse()
      setQuery('')
    } catch (_error) {
      // Creation failed — leave the create view open so the user can retry.
    } finally {
      setCreateSaving(false)
    }
  }

  const trimmedQuery = query.trim()
  const shownTags = useMemo(() => {
    const matching = trimmedQuery
      ? category.native_tags.filter(tag => matchTag(tag.name, trimmedQuery))
      : category.native_tags
    const pinned = matching.filter(tag => orderIds.includes(tag.id))
    const rest = matching.filter(tag => !orderIds.includes(tag.id))
    return [...pinned.sort(byName), ...rest.sort(byName)]
  }, [category.native_tags, trimmedQuery, orderIds])

  const exactMatch = category.native_tags.some(
    tag => tag.name.toLowerCase() === trimmedQuery.toLowerCase(),
  )

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger>
        <IconButton
          type="button"
          size="1"
          variant={open ? 'soft' : 'outline'}
          color="gray"
          className={styles.addTrigger}
          aria-label={`Add ${category.name.toLowerCase()} tags`}
        >
          <LuPlus size={14} />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content className={styles.popover} align="start">
        {step === 'create' ? (
          <form
            className={styles.createView}
            onSubmit={event => {
              event.preventDefault()
              commitCreate()
            }}
          >
            <Text as="p" size="1" weight="bold" className={styles.popoverTitle}>
              {`New ${category.name.toLowerCase()} tag`}
            </Text>
            <TextField.Root
              size="2"
              autoFocus
              placeholder={`Name the new ${category.name.toLowerCase()} tag…`}
              value={createName}
              onChange={event => setCreateName(event.target.value)}
            />
            <div className={styles.createViewFooter}>
              <Button
                type="button"
                size="1"
                variant="ghost"
                color="gray"
                onClick={backToBrowse}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="1"
                loading={createSaving}
                disabled={!createName.trim()}
              >
                Create
              </Button>
            </div>
          </form>
        ) : (
          <>
            <Text as="p" size="1" weight="bold" className={styles.popoverTitle}>
              {`Add ${category.name} tags`}
            </Text>
            <TextField.Root
              size="2"
              autoFocus
              placeholder={`Filter ${category.name.toLowerCase()}…`}
              value={query}
              onChange={event => setQuery(event.target.value)}
            >
              <TextField.Slot>
                <LuSearch size={14} />
              </TextField.Slot>
              {query && (
                <TextField.Slot side="right">
                  <IconButton
                    type="button"
                    size="1"
                    variant="ghost"
                    color="gray"
                    aria-label="Clear filter"
                    onClick={() => setQuery('')}
                  >
                    <LuX size={13} />
                  </IconButton>
                </TextField.Slot>
              )}
            </TextField.Root>
            <div className={styles.popoverFlow}>
              {shownTags.length === 0 && !trimmedQuery && (
                <Text size="1" color="gray" className={styles.popoverEmpty}>
                  No tags in this category
                </Text>
              )}
              {shownTags.map(tag => (
                <TagChip
                  key={tag.id}
                  name={tag.name}
                  color={colorIndicatorsEnabled ? tag.color : undefined}
                  ai={isAiTagPlaceholder(tag)}
                  selected={assignedTagIds.includes(tag.id)}
                  fresh={tag.id === freshTagId}
                  size="3"
                  onToggle={() => toggleTag(tag)}
                />
              ))}
              {trimmedQuery && !exactMatch && (
                <button
                  type="button"
                  className={styles.createChip}
                  onClick={() => openCreate(trimmedQuery)}
                >
                  <LuPlus size={12} />
                  {`Create “${trimmedQuery}”`}
                </button>
              )}
            </div>
            <div className={styles.popoverFooter}>
              <Button
                type="button"
                size="1"
                variant="ghost"
                onClick={() => openCreate('')}
              >
                Create tag
              </Button>
              <Popover.Close>
                <Button type="button" size="1">
                  Done
                </Button>
              </Popover.Close>
            </div>
          </>
        )}
      </Popover.Content>
    </Popover.Root>
  )
}

export default CategoryTagPicker
